import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { call, select } from 'redux-saga/effects';
import { PublishingOptions, RTCClient } from 'utils/webrtc';
import { RoomJoinedPayload } from 'features/room/types';
import { DeviceChangeMonitor } from 'utils/DeviceChangeMonitor';
import { isForceIceRelayLeverSet } from 'utils/webrtc/forceIceRelayLever';
import { selectIceRelayRequired } from 'features/application/applicationSlice';

/**
 * This saga handles various aspects of joining room and kicks off "init" process:
 *
 * creates media server connection
 * remembers whether we should do batch subscription to initial participants later on;
 * remembers users' publishing config to use later on
 **/
export function* establishJanusConnectionSaga(
  initData: RoomJoinedPayload,
  publishingOptions?: PublishingOptions
) {
  // Cleared here rather than on the join path only: the teardown that sets it
  // (RTCClient.destroy) also runs on signaling reconnect, and a stuck flag
  // permanently disables the subscriber's ICE restart.
  RTCClient.supressErrors = false;

  // this assignment and `initSubscriber` call need to happen before setting up mediaservers
  // because upon conecting to mediaserver we immediately try to subscribe and the `subscriber` needs to be configured
  if (initData.mediaServers.publishing.url !== initData.mediaServers.subscribing.url) {
    RTCClient.receiveMode = 'streaming';
  } else {
    RTCClient.receiveMode = 'videoroom';
  }

  const relayRequired: boolean = yield select(selectIceRelayRequired);

  const iceRelay = initData.iceRelay || relayRequired || isForceIceRelayLeverSet();

  yield call(DeviceChangeMonitor.start);

  yield call(RTCClient.receivingFeed.initReceiver, RTCClient.receiveMode);

  const broadcastAllowed: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.broadcastVideo, PermissionTypes.broadcastAudio],
    {
      mode: 'any',
    }
  );
  const screenshareAllowed: boolean = yield call(hasPermissionsSaga, PermissionTypes.screenshare);

  yield call(RTCClient.setupPublishState, publishingOptions, broadcastAllowed);
  yield call(RTCClient.setInitialSubscriptions, initData.users);

  if (broadcastAllowed || screenshareAllowed) {
    yield call(RTCClient.mediaServerConnector.connectToPublishingServerForMedia, 'publishing', {
      iceServers: initData.iceServers,
      mediaServer: initData.mediaServers.publishing,
      iceRelay,
    });

    yield call(
      RTCClient.mediaServerConnector.connectToSubscriptionServer,
      'base-subscription',
      {
        iceServers: initData.iceServers,
        mediaServer: initData.mediaServers.subscribing,
        iceRelay,
      },
      // mhm
      RTCClient.receiveMode === 'streaming' ? undefined : 'publishing'
    );
  } else {
    yield call(RTCClient.mediaServerConnector.connectToSubscriptionServer, 'base-subscription', {
      iceServers: initData.iceServers,
      mediaServer: initData.mediaServers.subscribing,
      iceRelay,
    });
  }
}
